import { Platform, View, Text, ScrollView, useWindowDimensions } from 'react-native'
import { Pressable } from '../../components/ui/Pressable'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { Image } from 'expo-image'
import { AuthFooterLink } from '../../components/auth/AuthFooterLink'
import { AuthGradientButton } from '../../components/auth/AuthGradientButton'
import Animated, { FadeIn, FadeInDown, FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { useAuth } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { useAuthBrandSplit } from '../../components/auth/BrandPanel'
import { LiquidCanvas } from '../../components/ui/LiquidBackdrop'
import { BREAKPOINTS } from '../../lib/theme/breakpoints'
import { TrustBadges } from '../../components/auth/TrustBadges'
import { SocialAuthRow } from '../../components/auth/SocialAuthRow'
import { isAuthInsurancePartnerTrustBadgeEnabled } from '../../lib/config/insurance-partners'
import { COLORS, FONTS, RADIUS, SPACING } from '../../lib/brand'
import {
  useSemanticColors,
  withAlpha,
  TEXT_STYLES,
  DURATION,
  STAGGER,
  INTERACTION,
} from '../../lib/theme/tokens'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import { useTheme } from '../../lib/hooks/use-theme'
import { impact, ImpactStyle } from '../../lib/utils/haptics'
import { toast } from '../../lib/toast'
import { captureError, errorMessage } from '../../lib/utils/observability'
import { isGoogleEmailExistsError } from '../../lib/errors/service-error'

// Legal pages: the in-app Pressables below route to /legal/terms-of-service
// and /legal/privacy-policy via expo-router (those screens live in
// app/legal/ and render the same content as the marketing site, with the
// app's chrome instead of a browser tab). Marketing site `tago.rent/{locale}/
// legal/*` exists for SEO and email-link landing.

/**
 * A 13" iPad in portrait used to arrive here: 1032x1376 clears the old raw
 * `width >= 900` test, so this screen handed a landscape-desktop composition
 * to a canvas shaped nothing like a desktop, and then tried to rescue it with
 * flexible band gaps that pushed the wordmark, the buttons and the legal line
 * to three separate corners of a 1376pt column.
 *
 * That machinery is gone with the case that needed it. `useAuthBrandSplit`
 * now requires landscape, so a portrait tablet takes the single-column branch
 * at the bottom of this file — the phone's composition, at a tablet's scale —
 * and the wide branch is once again only ever a wide, short window, where
 * plain centring is what it always wanted.
 */

export default function WelcomeScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { signInWithGoogle, signInWithApple, googlePending, applePending } = useAuth()
  const sessionExpired = useAuthStore((s) => s.sessionExpired)
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const { width, height } = useWindowDimensions()
  const insets = useSafeAreaInsets()

  const reducedMotion = useReducedMotion()
  const canUseEnteringAnimations = Platform.OS !== 'web' && !reducedMotion

  // Must be the same answer the auth layout gave, or this screen renders a
  // second brand column beside the layout's — or none at all.
  const isWide = useAuthBrandSplit()
  const isCompact = height < 700
  // The single-column branch below runs on everything from a 390pt phone to a
  // 1032pt portrait iPad. `isTabletCanvas` is the tablet tier of that one
  // composition: same anatomy and the same order, one step up the type and
  // measure scale, so the 13" capture reads as this app on a bigger screen
  // rather than as a phone screenshot stretched across one.
  const isTabletCanvas = !isWide && width >= BREAKPOINTS.md
  const logoSize = isCompact ? 88 : isTabletCanvas ? 172 : 104
  // Hoisted so the rhythm above the CTA can compensate when the badge block
  // is flagged off — otherwise the subtitle collapses onto the buttons.
  const showTrustBadges = isAuthInsurancePartnerTrustBadgeEnabled()

  const handleStart = () => {
    impact(ImpactStyle.Light)
    router.push('/(auth)/register')
  }

  const handleLogin = () => {
    router.push('/(auth)/login')
  }

  const handleSocial = async (
    provider: () => Promise<unknown>,
    providerName: 'google' | 'apple',
  ) => {
    // Same post-auth handoff as login: hold gate, unlock biometrics, seed
    // session, route by contact requirement. Welcome used to only await OAuth
    // and hope AuthGate noticed — cancel looked like a no-op and a sticky
    // biometric fail bounced success to /login.
    setCompletingSignup(true)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Welcome social is login intent, not register — clear any stale flag.
      window.sessionStorage.removeItem('tago.oauth.register_intent')
    }
    try {
      await provider()
      // Web OAuth redirects away before a session exists.
      if (Platform.OS === 'web') return

      const { finishSocialSignIn } = await import('../../lib/auth/finish-social-sign-in')
      const result = await finishSocialSignIn({
        router,
        screen: 'welcome',
        providerName,
        allowCancel: true,
      })
      if (result !== 'ok') {
        // Native cancel (account sheet dismissed) — silent, no toast.
        setCompletingSignup(false)
        return
      }
    } catch (err) {
      if (__DEV__) console.error(`[Welcome] ${providerName} sign-in failed:`, err)
      // The email already belongs to an account (email/password or phone). The
      // definitive recovery is to log in with that existing method — route to
      // the login screen with the reason instead of a dead-end toast here.
      if (isGoogleEmailExistsError(err)) {
        setCompletingSignup(false)
        toast.info(t('auth.errors.google_email_exists'))
        router.replace('/(auth)/login')
        return
      }
      const base = errorMessage(
        err,
        t(providerName === 'google' ? 'auth.errors.google_failed' : 'auth.errors.social_failed'),
      )
      // Surface a short machine code so support can map G12500 / rate limits
      // without Sentry. Strip noisy prefixes for the toast.
      toast.error(base)
      captureError(err, { screen: 'welcome', action: `${providerName}-sign-in` })
      setCompletingSignup(false)
    }
  }

  const handleGoogle = () => handleSocial(() => signInWithGoogle(), 'google')
  const handleApple = () => handleSocial(() => signInWithApple(), 'apple')

  const handlePhone = () => {
    router.push({ pathname: '/(auth)/phone-signup' as never, params: { mode: 'signup' } })
  }

  const loginFooter = (align: 'start' | 'center') => (
    <AuthFooterLink
      prompt={t('auth.already_have_account')}
      linkLabel={t('auth.login')}
      onPress={handleLogin}
      accessibilityLabel={`${t('auth.already_have_account')} ${t('auth.login')}`}
      testID="welcome.login-button"
      align={align}
    />
  )

  // ============================
  //  DESKTOP / WIDE LAYOUT (>=900px)
  //  At wide widths the auth _layout already renders BrandPanel, so the
  //  welcome route only renders the right-side form content.
  // ============================
  if (isWide) {
    const formContent = (
      <View
        style={{
          flex: 1,
          backgroundColor: colors.surface,
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: SPACING['2xl'],
        }}
      >
        <View style={{ width: '100%', maxWidth: 420 }}>
          <Animated.View
            collapsable={false}
            entering={canUseEnteringAnimations ? FadeIn.duration(DURATION.normal) : undefined}
            style={{ marginBottom: SPACING.sm, alignSelf: 'flex-start' }}
          >
            <Image
              source={require('../../assets/images/logo-horizontal.png')}
              style={{ width: 100, height: 32 }}
              contentFit="contain"
              accessibilityLabel={t('common.app_name')}
            />
          </Animated.View>

          {/* Subtitle */}
          <Animated.Text
            entering={
              canUseEnteringAnimations
                ? FadeInDown.duration(DURATION.normal).delay(STAGGER)
                : undefined
            }
            style={{
              fontFamily: FONTS.body,
              fontSize: 16,
              lineHeight: 24,
              // Secondary, like the mobile slogan. At textPrimary the
              // subtitle carried the same weight as the wordmark above it
              // and the header read as one flat block.
              color: colors.textSecondary,
              // The badge block below owns the gap when it renders; when the
              // flag is off the subtitle has to supply it itself.
              marginBottom: showTrustBadges ? SPACING.md : SPACING.xl,
            }}
          >
            {t('welcome.subheadline')}
          </Animated.Text>

          {showTrustBadges ? (
            <Animated.View
              collapsable={false}
              entering={
                canUseEnteringAnimations
                  ? FadeIn.duration(DURATION.normal).delay(STAGGER * 2)
                  : undefined
              }
              style={{ marginBottom: SPACING.xl, alignSelf: 'flex-start' }}
            >
              <TrustBadges isDark={isDark} align="start" />
            </Animated.View>
          ) : null}

          {/* Session expired banner */}
          {sessionExpired && (
            <Animated.View
              collapsable={false}
              entering={canUseEnteringAnimations ? FadeIn.duration(DURATION.normal) : undefined}
              testID="welcome.session-expired"
              style={{
                borderRadius: RADIUS.md,
                backgroundColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.2 : 0.08),
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.sm + SPACING.xs,
                marginBottom: SPACING.lg - SPACING.xs,
              }}
            >
              <Text
                style={{
                  textAlign: 'center',
                  fontSize: 14,
                  color: colors.textAccent,
                  fontFamily: FONTS.body,
                }}
              >
                {t('auth.session_expired')}
              </Text>
            </Animated.View>
          )}

          {/* Social first — Google is the zero-friction primary path.
              Email/password sits below the divider (same order as register). */}
          <Animated.View
            collapsable={false}
            entering={
              canUseEnteringAnimations
                ? FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
                : undefined
            }
          >
            <SocialAuthRow
              onGoogle={handleGoogle}
              onApple={handleApple}
              onPhone={handlePhone}
              googleLoading={googlePending}
              appleLoading={applePending}
              isDark={isDark}
            />
          </Animated.View>

          <Animated.View
            collapsable={false}
            entering={
              canUseEnteringAnimations
                ? FadeIn.duration(DURATION.normal).delay(STAGGER * 3)
                : undefined
            }
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginTop: SPACING.lg,
              marginBottom: SPACING.lg,
            }}
          >
            <View style={{ flex: 1, height: 1, backgroundColor: colors.border }} />
            <Text
              style={{
                marginHorizontal: SPACING.md,
                fontFamily: FONTS.body,
                fontSize: 13,
                letterSpacing: 0.2,
                color: colors.textSecondary,
              }}
            >
              {t('welcome.divider')}
            </Text>
            <View style={{ flex: 1, height: 1, backgroundColor: colors.border }} />
          </Animated.View>

          <AuthGradientButton
            title={t('welcome.cta_start')}
            onPress={handleStart}
            testID="welcome.create-account-button"
            entering={
              canUseEnteringAnimations
                ? FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
                : undefined
            }
          />

          {loginFooter('start')}

          {/* Legal */}
          <Animated.View
            collapsable={false}
            entering={
              canUseEnteringAnimations
                ? FadeIn.duration(DURATION.normal).delay(STAGGER * 7)
                : undefined
            }
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              // The links are 44pt boxes (below); without an explicit
              // cross-axis rule the plain runs of text stretch to that height
              // and sit on a different baseline than the links.
              alignItems: 'center',
              rowGap: SPACING.xs,
              marginTop: SPACING.lg,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                color: colors.textSecondary,
              }}
            >
              {t('welcome.legal_prefix')}
            </Text>
            {/* react-native-web drops hitSlop, so the box itself has to be
                the target: minHeight + centred content, same as mobile. */}
            <Pressable
              onPress={() => router.push('/legal/terms-of-service' as never)}
              accessibilityRole="link"
              accessibilityLabel={t('welcome.legal_terms')}
              hitSlop={8}
              testID="welcome.legal-terms-desktop"
              style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
            >
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  textDecorationLine: 'underline',
                  color: colors.textPrimary,
                }}
              >
                {t('welcome.legal_terms')}
              </Text>
            </Pressable>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                color: colors.textSecondary,
              }}
            >
              {t('welcome.legal_and')}
            </Text>
            <Pressable
              onPress={() => router.push('/legal/privacy-policy' as never)}
              accessibilityRole="link"
              accessibilityLabel={t('welcome.legal_privacy')}
              hitSlop={8}
              testID="welcome.legal-privacy-desktop"
              style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
            >
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  textDecorationLine: 'underline',
                  color: colors.textPrimary,
                }}
              >
                {t('welcome.legal_privacy')}
              </Text>
            </Pressable>
          </Animated.View>
        </View>
      </View>
    )

    // The auth layout already renders BrandPanel at this breakpoint —
    // only return the form column to avoid a double left panel.
    return (
      <LiquidCanvas>
        {/* `style="light"` fijo pintaba la hora y la bateria de BLANCO sobre un
            lienzo BLANCO: en este ramal el fondo es `colors.surface`, que en
            claro es #FFFFFF, igual que el BrandPanel de al lado. O sea que en un
            iPad en horizontal --el ramal se activa con ancho >= 900 y ancho >
            alto-- la barra de estado desaparecia en la PRIMERA pantalla de la
            app. Se deriva del tema, igual que el ramal angosto de abajo. */}
        <StatusBar style={isDark ? 'light' : 'dark'} />
        {formContent}
      </LiquidCanvas>
    )
  }

  // ============================
  //  MOBILE LAYOUT (unchanged)
  // ============================
  return (
    <LiquidCanvas>
      <SafeAreaView style={{ flex: 1, backgroundColor: 'transparent' }}>
        <StatusBar style={isDark ? 'light' : 'dark'} />

        {/* Language toggle — floated below the native status bar. Absolute
          children do not inherit SafeAreaView padding, so use insets
          directly and keep a real 44px+ touch target. */}
        <View
          style={{
            position: 'absolute',
            top: Math.max(insets.top + SPACING.sm, SPACING.xl),
            // Same gutter as the ScrollView below (SPACING.lg): at SPACING.md the
            // pill was the only element on the screen hanging 8pt past the right
            // edge of the button column.
            right: Math.max(insets.right + SPACING.lg, SPACING.lg),
            zIndex: 10,
          }}
        >
          <Pressable
            onPress={() => {
              void i18n.changeLanguage(i18n.language?.toLowerCase().startsWith('en') ? 'es' : 'en')
            }}
            accessibilityRole="button"
            accessibilityLabel={t('common.toggle_language')}
            testID="welcome.language-toggle"
            hitSlop={8}
            style={{
              minWidth: 56,
              minHeight: 44,
              paddingHorizontal: SPACING.sm + SPACING.xs,
              borderRadius: RADIUS.full,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
              // shadow* props are deprecated on RN Web and were logging a
              // deprecation warning on the first screen of the app.
              ...WEB_SHADOWS.md,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 12,
                lineHeight: 16,
                color: colors.textPrimary,
              }}
            >
              {/* No chevron: this is a two-state toggle, not a menu that opens
                a list, and the caret promised a picker that does not exist. */}
              {i18n.language?.toLowerCase().startsWith('en') ? 'EN' : 'ES'}
            </Text>
          </Pressable>
        </View>

        {/* Session expired banner */}
        {sessionExpired && (
          <Animated.View
            collapsable={false}
            entering={canUseEnteringAnimations ? FadeIn.duration(DURATION.normal) : undefined}
            testID="welcome.session-expired"
            className="mx-6 mt-2 rounded-xl bg-coral/10 px-4 py-3 dark:bg-coral/20"
          >
            <Text className="text-center text-sm text-coral">{t('auth.session_expired')}</Text>
          </Animated.View>
        )}

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            paddingHorizontal: SPACING.lg,
            paddingTop: isCompact ? SPACING.xl : SPACING['2xl'],
            paddingBottom:
              Math.max(insets.bottom, SPACING.xl) + (isCompact ? SPACING['2xl'] : SPACING.xl),
          }}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={{
              width: '100%',
              // The measure grows with the canvas but stops well short of it:
              // 600pt on a 1032pt iPad keeps the CTA a button rather than a
              // banner, and holds the headline to the two lines it breaks to on
              // a phone. Surplus lands as equal margin on both sides; brand
              // copy and the mark share a centred vertical axis inside it.
              maxWidth: isTabletCanvas ? 640 : 520,
              alignSelf: 'center',
            }}
          >
            <Animated.View
              collapsable={false}
              entering={canUseEnteringAnimations ? FadeInDown.duration(DURATION.slow) : undefined}
              style={{
                // Single-column phone/tablet: brand mark, headline and slogan
                // share one centred axis. Left-edged lockup was a leftover from
                // the desktop editorial column and read as broken on a phone.
                alignSelf: 'center',
                width: logoSize,
                height: logoSize,
                marginBottom: isCompact ? SPACING.lg : isTabletCanvas ? SPACING['2xl'] : SPACING.xl,
              }}
            >
              <Image
                source={require('../../assets/images/logo-stacked.png')}
                style={{ width: logoSize, height: logoSize }}
                contentFit="contain"
                accessibilityLabel={t('common.app_name')}
              />
            </Animated.View>

            <Animated.Text
              entering={
                canUseEnteringAnimations
                  ? FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
                  : undefined
              }
              numberOfLines={2}
              style={{
                // Heavitas, in caps — the brand face. This is the line the iPad
                // used to lose: the split branch had no headline of its own, so
                // the left panel answered for it in Montserrat and the first
                // App Store screenshot spoke in a voice the app does not use.
                ...TEXT_STYLES.display,
                // 52pt sets the longer line ("PRÓXIMO RIDE.") to ~422pt inside a
                // 640pt measure, so the two-line break the phone gets is the two
                // -line break the tablet gets, with ~34% of the measure still in
                // hand for a longer locale before `numberOfLines={2}` could bite.
                fontSize: isCompact ? 30 : isTabletCanvas ? 52 : 36,
                lineHeight: isCompact ? 36 : isTabletCanvas ? 62 : 44,
                textAlign: 'center',
                color: colors.textPrimary,
              }}
            >
              {t('welcome.headline')}
            </Animated.Text>

            <Animated.Text
              entering={
                canUseEnteringAnimations
                  ? FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
                  : undefined
              }
              style={{
                fontFamily: FONTS.body,
                fontSize: isCompact ? 15 : isTabletCanvas ? 20 : 17,
                lineHeight: isCompact ? 21 : isTabletCanvas ? 30 : 25,
                marginTop: SPACING.md,
                marginBottom: isCompact ? SPACING.lg : isTabletCanvas ? SPACING['2xl'] : SPACING.xl,
                color: colors.textSecondary,
                textAlign: 'center',
              }}
            >
              {t('welcome.slogan')}
            </Animated.Text>

            <Animated.View
              collapsable={false}
              entering={
                canUseEnteringAnimations
                  ? FadeInUp.duration(DURATION.normal).delay(STAGGER * 10)
                  : undefined
              }
              style={{ width: '100%' }}
            >
              {/* Social first (Google primary). Email create + login below. */}
              <SocialAuthRow
                onGoogle={handleGoogle}
                onApple={handleApple}
                onPhone={handlePhone}
                googleLoading={googlePending}
                appleLoading={applePending}
                isDark={isDark}
              />

              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginTop: isCompact ? SPACING.md : SPACING.lg,
                  marginBottom: isCompact ? SPACING.md : SPACING.lg,
                }}
              >
                <View style={{ flex: 1, height: 1, backgroundColor: colors.border }} />
                <Text
                  style={{
                    marginHorizontal: SPACING.md,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    letterSpacing: 0.2,
                    color: colors.textSecondary,
                  }}
                >
                  {t('welcome.divider')}
                </Text>
                <View style={{ flex: 1, height: 1, backgroundColor: colors.border }} />
              </View>

              <AuthGradientButton
                title={t('welcome.cta_start')}
                onPress={handleStart}
                testID="welcome.create-account-button"
              />

              {loginFooter('center')}

              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: isCompact ? SPACING.md : SPACING.lg,
                  rowGap: SPACING.xs,
                }}
              >
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                  {t('welcome.legal_prefix')}
                </Text>
                <Pressable
                  onPress={() => router.push('/legal/terms-of-service' as never)}
                  accessibilityRole="link"
                  accessibilityLabel={t('welcome.legal_terms')}
                  hitSlop={8}
                  testID="welcome.legal-terms-mobile"
                  style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 13,
                      textDecorationLine: 'underline',
                      color: colors.textPrimary,
                    }}
                  >
                    {t('welcome.legal_terms')}
                  </Text>
                </Pressable>
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                  {t('welcome.legal_and')}
                </Text>
                <Pressable
                  onPress={() => router.push('/legal/privacy-policy' as never)}
                  accessibilityRole="link"
                  accessibilityLabel={t('welcome.legal_privacy')}
                  hitSlop={8}
                  testID="welcome.legal-privacy-mobile"
                  style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 13,
                      textDecorationLine: 'underline',
                      color: colors.textPrimary,
                    }}
                  >
                    {t('welcome.legal_privacy')}
                  </Text>
                </Pressable>
              </View>

              {showTrustBadges ? (
                <Animated.View
                  collapsable={false}
                  entering={
                    canUseEnteringAnimations
                      ? FadeIn.duration(DURATION.normal).delay(STAGGER * 11)
                      : undefined
                  }
                  style={{
                    marginTop: isCompact ? SPACING.lg : SPACING.xl,
                    alignSelf: 'center',
                  }}
                >
                  <TrustBadges isDark={isDark} align="center" />
                </Animated.View>
              ) : null}
            </Animated.View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </LiquidCanvas>
  )
}
